import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate, useReducedMotion } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

/* Scroll-reveal wrapper — fade up once when entering viewport */
export function Reveal({ children, delay = 0, y = 40, className = "", ...rest }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/* Signature kinetic headline — masked line-by-line reveal */
export function MaskedLines({ lines, delay = 0, stagger = 0.12 }) {
  return (
    <>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
          <motion.span
            className="block origin-left will-change-transform"
            initial={{ y: "115%", rotate: 3 }}
            animate={{ y: "0%", rotate: 0 }}
            transition={{ duration: 1.15, ease: EASE, delay: delay + i * stagger }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </>
  );
}

/* Count-up number that triggers when scrolled into view */
export function CountUp({ to, suffix = "", duration = 1.8, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reduce = useReducedMotion();
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setVal(to);
      return;
    }
    const controls = animate(0, to, {
      duration,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration, reduce]);

  return (
    <span ref={ref} className={className}>
      {val}
      {suffix}
    </span>
  );
}

/* Numbered manifesto chapter marker */
export function ChapterTag({ n, label }) {
  return (
    <div className="flex items-center gap-4">
      <span className="font-display text-4xl md:text-5xl font-light text-[#D4AF37]/90 tracking-tighter">{n}</span>
      <span className="w-10 h-px bg-[#D4AF37]/50" aria-hidden />
      <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-slate-400 font-mono">{label}</span>
    </div>
  );
}

/* Pointer-tracking spotlight for cards (radial gold glow follows mouse) */
export function useSpotlight() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: -600, y: -600 });
  const onMouseMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
  };
  return { ref, pos, onMouseMove };
}
